(() => {
  const initialize = (scope = document) => {
    const sections = scope.matches?.('[data-inara-product]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-inara-product]'));

    sections.forEach((section) => {
      if (section.dataset.inaraInitialized) return;
      const dataElement = section.querySelector('[data-product-data]');
      if (!dataElement) return;
      let data;
      try {
        data = JSON.parse(dataElement.textContent);
      } catch {
        return;
      }
      section.dataset.inaraInitialized = 'true';

      const track = section.querySelector('[data-gallery-track]');
      const slides = Array.from(section.querySelectorAll('[data-gallery-slide]'));
      const thumbnails = Array.from(section.querySelectorAll('[data-gallery-thumbnail]'));
      const thumbnailTrack = section.querySelector('[data-gallery-thumbnails]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.hasAttribute('data-initial-image')));
      let scrollFrame;

      const updateThumbnails = (index) => {
        const changed = activeIndex !== index;
        activeIndex = index;
        thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const active = thumbnailIndex === index;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-pressed', String(active));
        });
        const selected = thumbnails[index];
        if (selected && thumbnailTrack && changed) {
          const viewport = thumbnailTrack.getBoundingClientRect();
          const bounds = selected.getBoundingClientRect();
          const start = bounds.left - viewport.left + thumbnailTrack.scrollLeft - 4;
          const end = bounds.right - viewport.right + thumbnailTrack.scrollLeft + 4;
          if (bounds.left < viewport.left || bounds.right > viewport.right) {
            thumbnailTrack.scrollTo({ left: bounds.left < viewport.left ? start : end, behavior: 'auto' });
          }
        }
      };

      const showImage = (index, behavior = reducedMotion ? 'auto' : 'smooth') => {
        if (!track || !slides.length) return;
        const nextIndex = (index + slides.length) % slides.length;
        const target = slides[nextIndex].offsetLeft - slides[0].offsetLeft;
        track.scrollTo({ left: target, behavior });
        updateThumbnails(nextIndex);
      };

      if (track && slides.length) {
        requestAnimationFrame(() => showImage(activeIndex, 'auto'));
        thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', () => showImage(Number(thumbnail.dataset.galleryThumbnail))));
        section.querySelector('[data-gallery-previous]')?.addEventListener('click', () => showImage(activeIndex - 1));
        section.querySelector('[data-gallery-next]')?.addEventListener('click', () => showImage(activeIndex + 1));
        track.addEventListener('scroll', () => {
          cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(() => {
            const width = slides[0].getBoundingClientRect().width;
            if (width) updateThumbnails(Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / width))));
          });
        }, { passive: true });
        track.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          showImage(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
        });
        const resizeObserver = new ResizeObserver(() => showImage(activeIndex, 'auto'));
        resizeObserver.observe(track);
      }

      const variantSelect = section.querySelector('[data-variant-select]');
      const optionContainer = section.querySelector('[data-product-options]');
      const price = section.querySelector('[data-product-price]');
      const compare = section.querySelector('[data-product-compare-price]');
      const submit = section.querySelector('[data-product-submit]');
      const submitLabel = section.querySelector('[data-product-submit-label]');
      const stock = section.querySelector('[data-product-stock]');
      const stockLabel = section.querySelector('[data-product-stock-label]');
      if (!variantSelect || !data.variants?.length) return;

      if (optionContainer) {
        optionContainer.hidden = false;
        const nativeVariants = section.querySelector('[data-native-variants]');
        if (nativeVariants) nativeVariants.hidden = true;
      }

      const updateVariant = (variant, updateUrl = false, updateImage = false) => {
        const inventoryState = variant
          ? data.inventoryStates?.[String(variant.id)] || (variant.available ? 'inStock' : 'soldOut')
          : 'unavailable';
        if (stock) stock.dataset.state = inventoryState;
        if (stockLabel) stockLabel.textContent = data.labels[inventoryState] || data.labels.unavailable;
        if (!variant) {
          variantSelect.value = '';
          price.textContent = data.labels.unavailable;
          compare.hidden = true;
          submit.disabled = true;
          submitLabel.textContent = data.labels.unavailable;
          return;
        }
        variantSelect.value = String(variant.id);
        const formatted = data.prices[String(variant.id)];
        if (formatted) {
          price.textContent = formatted.price;
          compare.hidden = !formatted.compare;
          compare.textContent = formatted.compare || '';
        }
        submit.disabled = !variant.available;
        submitLabel.textContent = variant.available ? data.labels.add : data.labels.soldOut;
        const imageId = variant.featured_image?.id || variant.featured_media?.preview_image?.id;
        if (updateImage && imageId) {
          const imageIndex = slides.findIndex((slide) => slide.dataset.imageId === String(imageId));
          if (imageIndex >= 0) showImage(imageIndex);
        }
        if (updateUrl && !window.Shopify?.designMode) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url);
        }
      };

      const optionFieldsets = Array.from(optionContainer?.querySelectorAll('[data-option-position]') || []);
      const updateOptionLabels = () => {
        optionFieldsets.forEach((fieldset) => {
          const selectedLabel = fieldset.querySelector('[data-option-selected]');
          if (selectedLabel) selectedLabel.textContent = fieldset.querySelector('input:checked')?.value || '';
        });
      };

      optionContainer?.addEventListener('change', (event) => {
        if (!event.target.matches('[data-option-value]')) return;
        updateOptionLabels();
        const options = optionFieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value);
        const selectedVariant = data.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
        updateVariant(selectedVariant, true, true);
      });

      updateOptionLabels();
      const initialVariant = data.variants.find((variant) => String(variant.id) === variantSelect.value);
      updateVariant(initialVariant);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

